/**
 * The catalogue tree, and which ERP family falls where in it.
 *
 * One screen for two things, because they are one job: the tree exists so that
 * families can be pointed at it, and a family with no category makes its
 * articles unpublishable. Splitting them across two pages would mean building
 * a category, navigating away, and coming back to use it.
 *
 * **The tree on the left, a panel on the right.** Selecting a node opens it in
 * the panel — name, level, parent, order, publication, ERP families — and the
 * tree stays in view, because every decision there is about where the node
 * sits among the others. Creating is the same panel: "+" beside a node creates
 * beneath it, the header button creates beneath the selected node or at the
 * root when nothing is selected. A code is never typed: it is derived from the
 * path, as the import derives it.
 *
 * **The unmapped families lead.** The list is sorted with them first and the
 * count is in the page description, because the screen's purpose is what is
 * *not* done. On the delivered extract that is 14 families out of 57 — `AGR`,
 * `PSR`, `LVR` and the rest, whose meaning nobody here knows — and a list
 * sorted alphabetically would bury them among the 43 that are finished.
 *
 * **A deactivated category reads as unmapped.** Its articles are no longer
 * reachable in the tree, so that is the truth, and the API says so; the screen
 * does not second-guess it.
 *
 * **The levels have names, and the tree can be loaded from a sheet.** What
 * each depth is called — Activité, Famille, Sous-famille, Segment — is the
 * installation's configuration, typed here. The import finds the sheet's
 * columns by those names, under whatever title block it opens with, and only
 * ever adds: a node already in the tree is left exactly as somebody made it.
 *
 * **The brand is an axis of its own**, not a fifth level: one list, spelled
 * once, offered as the family's default brand here and on each article.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import { ApiError, type ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import {
  Button,
  Card,
  DataTable,
  Dialog,
  Input,
  Page,
  Select,
  StatusDot,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { CatalogApi, type CategoryNode, type FamilyMapping } from "../api";
import { NAMESPACE } from "../i18n";
import { CATALOG_PATHS } from "../paths";
import { CategoryPanel, type PanelMode } from "./categories/CategoryPanel";
import { CategoryTree } from "./categories/CategoryTree";
import {
  MAX_DEPTH,
  branchOf,
  familiesByCategory,
  findNode,
  flatten,
  unmappedHeads,
} from "./categories/tree";
import "./catalog.css";

/** Where the empty state's import action leads. */
const IMPORT_ID = "categories-import";

export function CategoriesScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new CatalogApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("catalog.category.write");

  const treeFetcher = useCallback(() => api.categoryTree(true), [api]);
  const tree = useResource(treeFetcher);
  const mappingFetcher = useCallback(() => api.familyMappings(), [api]);
  const mappings = useResource(mappingFetcher);
  const levelsFetcher = useCallback(() => api.levels(), [api]);
  const levels = useResource(levelsFetcher);
  const groupsFetcher = useCallback(() => api.attributeGroups(false), [api]);
  const groups = useResource(groupsFetcher);
  const brandsFetcher = useCallback(() => api.brands(true), [api]);
  const brands = useResource(brandsFetcher);

  const [mode, setMode] = useState<PanelMode>({ kind: "none" });
  const [deleting, setDeleting] = useState<CategoryNode | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const roots = useMemo(() => tree.data?.roots ?? [], [tree.data]);
  const familyRows = useMemo(() => mappings.data?.items ?? [], [mappings.data]);
  const options = useMemo(() => flatten(roots), [roots]);
  const families = useMemo(() => familiesByCategory(familyRows), [familyRows]);
  const unmappedNodes = useMemo(() => unmappedHeads(roots, families), [roots, families]);
  const levelLabel = (depth: number) =>
    levels.data?.items.find((item) => item.depth === depth)?.label ?? "";
  const selected = mode.kind === "detail" ? findNode(roots, mode.id) : undefined;

  // On a narrow screen the panel sits under the tree: bring it into view when
  // it changes, or a click on a node would seem to do nothing.
  function show(next: PanelMode) {
    setMode(next);
    requestAnimationFrame(() => {
      if (window.matchMedia?.("(width < 64rem)").matches) {
        panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }

  // What the confirmation announces, read from what the screen already holds:
  // the branch beneath the node, the articles classed in it, and the families
  // mapped onto it — the three things the cascade touches.
  const doomed = deleting ? branchOf(deleting) : [];
  const doomedPaths = new Set(doomed.map((node) => node.category.path));
  const consequences = deleting
    ? [
        ...(doomed.length > 1
          ? [t("categories.deletion.children", { count: doomed.length - 1 })]
          : []),
        ...(() => {
          const articles = doomed.reduce((sum, node) => sum + node.article_count, 0);
          return articles > 0 ? [t("categories.deletion.articles", { count: articles })] : [];
        })(),
        ...(() => {
          const count = familyRows.filter((row) => doomedPaths.has(row.category_path)).length;
          return count > 0 ? [t("categories.deletion.families", { count })] : [];
        })(),
      ]
    : [];

  function reloadBoth() {
    tree.reload();
    mappings.reload();
  }

  const activeBrands = (brands.data?.items ?? []).filter((item) => item.is_active);

  const columns: Column<FamilyMapping>[] = [
    {
      key: "family",
      header: t("categories.column.family"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <code>{row.family_code}</code>
          <span className="hardauto-cell__muted">{row.family_label}</span>
        </div>
      ),
    },
    {
      key: "category",
      header: t("categories.column.category"),
      render: (row) =>
        mayWrite ? (
          <label className="hardauto-cell__control">
            {/* The label is visually hidden rather than absent: a screen-reader
                user landing on a bare select in a table of 57 rows has no way
                to know which family it belongs to. */}
            <span className="hardauto-visually-hidden">
              {t("categories.mapLabel", { family: row.family_code })}
            </span>
            <Select
              value={row.category_id ?? ""}
              disabled={action.isBusy(row.family_code)}
              onChange={(event) => {
                const next = event.target.value;
                void action.run(row.family_code, async () => {
                  try {
                    if (next) {
                      await api.mapFamily(row.family_code, next);
                      toast.show(t("categories.mapped"), { tone: "success" });
                    } else {
                      await api.unmapFamily(row.family_code);
                      toast.show(t("categories.unmapped_done"), { tone: "success" });
                    }
                    reloadBoth();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                });
              }}
            >
              <option value="">{t("categories.mapPlaceholder")}</option>
              {options.map(({ node, depth }) => (
                <option key={node.category.id} value={node.category.id}>
                  {`${"  ".repeat(depth)}${node.category.label}`}
                </option>
              ))}
            </Select>
          </label>
        ) : (
          <span>{row.category_label || t("fiches.noCategory")}</span>
        ),
    },
    {
      key: "articles",
      header: t("categories.column.articles"),
      render: (row) => (
        <span className="hardauto-num">{row.article_count.toLocaleString(i18n.language)}</span>
      ),
    },
    {
      key: "required",
      header: t("categories.column.required"),
      render: (row) => (
        <span className="hardauto-cell__muted">
          {row.required_attributes.length === 0
            ? t("categories.noRequired")
            : row.required_attributes.join(", ")}
        </span>
      ),
    },
    {
      key: "brand",
      header: t("categories.column.brand"),
      render: (row) =>
        mayWrite && row.category_id ? (
          <label className="hardauto-cell__control">
            <span className="hardauto-visually-hidden">
              {t("categories.brandLabel", { family: row.family_code })}
            </span>
            <Select
              value={row.brand ?? ""}
              disabled={action.isBusy(`brand-${row.family_code}`)}
              onChange={(event) => {
                const next = event.target.value;
                const categoryId = row.category_id;
                if (!categoryId) return;
                void action.run(`brand-${row.family_code}`, async () => {
                  try {
                    await api.mapFamily(row.family_code, categoryId, next);
                    toast.show(t("categories.brandSaved"), { tone: "success" });
                    mappings.reload();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                });
              }}
            >
              <option value="">{t("categories.noBrand")}</option>
              {row.brand && !activeBrands.some((item) => item.label === row.brand) ? (
                <option value={row.brand}>{row.brand}</option>
              ) : null}
              {activeBrands.map((item) => (
                <option key={item.id} value={item.label}>
                  {item.label}
                </option>
              ))}
            </Select>
          </label>
        ) : (
          <span className="hardauto-cell__muted">{row.brand || "—"}</span>
        ),
    },
    { key: "unit", header: t("categories.column.unit"), render: (row) => row.default_unit },
    {
      key: "state",
      header: t("categories.column.state"),
      render: (row) => (
        <StatusDot
          label={row.is_mapped ? t("categories.state.mapped") : t("categories.state.unmapped")}
          tone={row.is_mapped ? "success" : "warning"}
        />
      ),
    },
  ];

  const unmapped = mappings.data?.unmapped ?? 0;

  // The header button follows the selection: beneath the selected node when
  // it can have children, at the root otherwise.
  const createUnder = selected && selected.category.depth < MAX_DEPTH ? selected.category.id : null;

  function goToImport() {
    const target = document.getElementById(IMPORT_ID);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
    target?.querySelector<HTMLInputElement>("input[type=file]")?.focus();
  }

  return (
    <Page
      title={t("categories.title")}
      description={t("categories.description")}
      eyebrow={
        // Nothing to report until there are families: "every family is
        // mapped" over an empty list reads as a success that did not happen.
        familyRows.length > 0
          ? unmapped > 0
            ? t("categories.unmapped", { count: unmapped })
            : t("categories.allMapped")
          : undefined
      }
      actions={
        mayWrite ? (
          <Button variant="primary" onClick={() => show({ kind: "create", parentId: createUnder })}>
            {createUnder ? t("tree.createChild") : t("panel.createRoot")}
          </Button>
        ) : undefined
      }
    >
      <div className="hardauto-catlayout">
        <Card title={t("categories.tree")} compactTitle>
          {tree.loading && !tree.data ? (
            <p className="hardauto-cell__muted">{t("categories.loading")}</p>
          ) : roots.length === 0 ? (
            <div className="hardauto-catempty">
              <strong>{t("tree.emptyTitle")}</strong>
              <p>{t("tree.emptyBody")}</p>
              {mayWrite ? (
                <div className="hardauto-catempty__actions">
                  <Button
                    variant="primary"
                    onClick={() => show({ kind: "create", parentId: null })}
                  >
                    {t("panel.createRoot")}
                  </Button>
                  <Button onClick={goToImport}>{t("tree.importAction")}</Button>
                </div>
              ) : null}
            </div>
          ) : (
            <CategoryTree
              roots={roots}
              locale={i18n.language}
              selectedId={selected?.category.id ?? null}
              families={families}
              unmapped={unmappedNodes}
              levelLabel={levelLabel}
              mayWrite={mayWrite}
              onSelect={(node) => show({ kind: "detail", id: node.category.id })}
              onAddChild={(node) => show({ kind: "create", parentId: node.category.id })}
            />
          )}
        </Card>

        <div className="hardauto-catlayout__panel" ref={panelRef}>
          <CategoryPanel
            api={api}
            mode={mode}
            roots={roots}
            mappings={familyRows}
            groups={groups.data?.items ?? []}
            levelLabel={levelLabel}
            mayWrite={mayWrite}
            onChanged={reloadBoth}
            onGroupsChanged={groups.reload}
            onCreated={(id) => setMode({ kind: "detail", id })}
            onCancel={() =>
              setMode(selected ? { kind: "detail", id: selected.category.id } : { kind: "none" })
            }
            onDelete={setDeleting}
            onCreateRoot={() => show({ kind: "create", parentId: null })}
          />
        </div>
      </div>

      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={t("categories.deletion.title", { label: deleting?.category.label ?? "" })}
        closeLabel={t("categories.cancel")}
        footer={
          <>
            <Button onClick={() => setDeleting(null)}>{t("categories.cancel")}</Button>
            <Button
              variant="danger"
              busy={action.isBusy("delete")}
              busyLabel={t("categories.deleting")}
              onClick={() => {
                if (!deleting) return;
                void action.run("delete", async () => {
                  try {
                    const outcome = await api.deleteCategory(deleting.category.id);
                    toast.show(
                      t("categories.deletion.done", {
                        label: deleting.category.label,
                        count: outcome.categories,
                      }),
                      { tone: "success" },
                    );
                    setDeleting(null);
                    setMode({ kind: "none" });
                    reloadBoth();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                });
              }}
            >
              {t("categories.deletion.confirm")}
            </Button>
          </>
        }
      >
        <p className="hardauto-catalog__note">{t("categories.deletion.irreversible")}</p>
        {consequences.length > 0 ? (
          <ul className="hardauto-catalog__consequences">
            {consequences.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}
        <p className="hardauto-cell__muted">{t("categories.deletion.hideInstead")}</p>
      </Dialog>

      <Card title={t("categories.mapping")} compactTitle flush>
        <DataTable<FamilyMapping>
          caption={t("categories.mappingCaption")}
          columns={columns}
          rows={familyRows}
          rowKey={(row) => row.family_code}
          loading={mappings.loading}
          loadingLabel={t("categories.loading")}
          error={
            mappings.failed
              ? {
                  title: t("error.title"),
                  onRetry: mappings.reload,
                  retryLabel: t("error.retry"),
                }
              : undefined
          }
          empty={{ title: t("categories.empty.title"), description: t("categories.empty.body") }}
        />
      </Card>

      <div className="hardauto-catalog__split" id={IMPORT_ID}>
        <LevelsCard
          api={api}
          labels={(levels.data?.items ?? []).map((item) => item.label)}
          mayWrite={mayWrite}
          onSaved={levels.reload}
          onImported={() => {
            reloadBoth();
            brands.reload();
          }}
        />
        <Card title={t("brands.title")} compactTitle>
          <p className="hardauto-cell__muted">{t("brands.moved")}</p>
          <Link to={CATALOG_PATHS.brands}>{t("brands.open")}</Link>
        </Card>
      </div>
    </Page>
  );
}

/**
 * The names of the tree's levels, and the import that reads a sheet by them.
 *
 * Together because the second depends on the first: the import finds its
 * columns by these names, so naming the levels is the first step of loading
 * a tree, and an unnamed tree cannot be imported.
 */
function LevelsCard({
  api,
  labels,
  mayWrite,
  onSaved,
  onImported,
}: {
  api: CatalogApi;
  labels: string[];
  mayWrite: boolean;
  onSaved: () => void;
  onImported: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const values = draft ?? [0, 1, 2, 3].map((index) => labels[index] ?? "");

  return (
    <Card title={t("levels.title")} description={t("levels.description")} compactTitle>
      <div className="hardauto-stack">
        <form
          className="hardauto-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            void action.run("levels", async () => {
              try {
                await api.nameLevels(values.map((value) => value.trim()));
                setDraft(null);
                toast.show(t("levels.saved"), { tone: "success" });
                onSaved();
              } catch (error) {
                toast.show(error instanceof ApiError ? error.message : t("error.title"), {
                  tone: "danger",
                });
              }
            });
          }}
        >
          {values.map((value, index) => (
            <div className="hardauto-inline-form__field" key={index}>
              <Field label={t("levels.level", { depth: index + 1 })}>
                {(props) => (
                  <Input
                    {...props}
                    value={value}
                    disabled={!mayWrite}
                    onChange={(event) => {
                      const next = [...values];
                      next[index] = event.target.value;
                      setDraft(next);
                    }}
                  />
                )}
              </Field>
            </div>
          ))}
          {mayWrite ? (
            <div className="hardauto-inline-form__actions">
              <Button type="submit" busy={action.isBusy("levels")} disabled={draft === null}>
                {t("levels.save")}
              </Button>
            </div>
          ) : null}
        </form>

        {mayWrite ? (
          <form
            className="hardauto-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!file) return;
              void action.run("import", async () => {
                try {
                  const result = await api.importTree(file, labels, t("levels.brandColumn"));
                  toast.show(
                    t("levels.imported", {
                      created: result.created,
                      present: result.already_present,
                      brands: result.brands_created,
                    }),
                    { tone: "success" },
                  );
                  setFile(null);
                  onImported();
                } catch (error) {
                  toast.show(error instanceof ApiError ? error.message : t("error.title"), {
                    tone: "danger",
                  });
                }
              });
            }}
          >
            <div className="hardauto-inline-form__field hardauto-inline-form__field--wide">
              <Field label={t("levels.file")} hint={t("levels.fileHint")}>
                {(props) => (
                  <Input
                    {...props}
                    type="file"
                    accept=".xlsx,.csv"
                    disabled={labels.length === 0}
                    onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                  />
                )}
              </Field>
            </div>
            <div className="hardauto-inline-form__actions">
              <Button
                type="submit"
                variant="primary"
                busy={action.isBusy("import")}
                disabled={!file || labels.length === 0}
              >
                {t("levels.import")}
              </Button>
            </div>
          </form>
        ) : null}
      </div>
    </Card>
  );
}
